import { useMemo } from 'react'

import { capitalize, isNil } from 'lodash-es'

import { TREATMENT_OPTIONS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { getSessionLimitProgramCategoryByTreatmentOption } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { PROGRAMS } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { getLicenseProgramType } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { LTProgramName } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ProgramCategory } from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'
import { YearProgramCoverage } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { useProgramCoverageBreakdown } from './useProgramCoverageBreakdown'
import { healthPlanStates } from '../../../../common/constants/appConstants'

export interface UseSessionLimitInfoArgs {
  clientDetails?: ClientObject
  programName?: LTProgramName
}

const LICENSE_BASED_PROGRAM_TYPE_DISPLAY_NAMES: Record<string, string> = {
  manager_coaching: 'manager growth coaching',
}

export interface SessionLimitInfo {
  /** Render the low-sessions-remaining warning banner. */
  showLowSessionsWarning: boolean
  /** Render the health-plan limit info text. */
  showHealthPlanLimitInfo: boolean
  /** Render the low-license-sessions-remaining warning banner. */
  showLowLicenseSessionWarning: boolean
  /** Copy for the low-sessions-remaining warning banner. */
  lowSessionsRemainingText: string
  /** Copy for the health-plan limit info text. */
  healthPlanLimitInfoText: string
  /** Copy for the low-license-sessions-remaining warning banner. */
  lowLicenseSessionRemainingText: string
}

const WARNING_THRESHOLD = 5

export const useSessionLimitInfo = ({ clientDetails, programName }: UseSessionLimitInfoArgs): SessionLimitInfo => {
  const program = programName ? PROGRAMS[programName] : undefined
  const customerPropertyId = program?.customerPropertyId
  const programCoverageBreakdown = useProgramCoverageBreakdown(clientDetails?.employer, clientDetails?.country)

  const { health_plan_status: healthPlanStatus, open_bhb_session_limit: openBhbSessionLimit } = clientDetails ?? {}

  const { seat_based_licenses: seatBasedLicenses } = clientDetails ?? {}

  const licenseProgramType = getLicenseProgramType(programName)
  const isLicenseBased = !!licenseProgramType
  const licenseBasedSessionInfo = licenseProgramType
    ? seatBasedLicenses?.find((license) => license.program_type === licenseProgramType)
    : undefined
  const isLowLicenseSessionRemaining = licenseBasedSessionInfo
    ? licenseBasedSessionInfo.sessions_remaining < WARNING_THRESHOLD && licenseBasedSessionInfo.sessions_remaining > 0
    : false

  const { usedSessionCount, sessionsLimit, sessionsRemaining, programType } = useMemo(() => {
    let usedSessionCount = clientDetails?.sessions_used
    let sessionsLimit = clientDetails?.sessions_limit
    let sessionsRemaining = clientDetails?.sessions_remaining ?? 0
    const sessionLimitByProgram = clientDetails?.sessions_limit_by_program
    const sessionUsedByProgram = clientDetails?.sessions_used_by_program
    let programType: ProgramCategory | null = null
    if (sessionLimitByProgram) {
      // TODO: Potential bug here: customerPropertyId is being used as directly as a TREATMENT_OPTIONS_TYPE
      programType = getSessionLimitProgramCategoryByTreatmentOption(
        customerPropertyId as unknown as TREATMENT_OPTIONS_TYPE,
      )
      usedSessionCount = sessionUsedByProgram?.[programType] ?? 0
      sessionsLimit = sessionLimitByProgram[programType] ?? 0
      sessionsRemaining = Math.max(sessionsLimit - usedSessionCount, 0)
    }
    return { usedSessionCount, sessionsLimit, sessionsRemaining, programType }
  }, [
    clientDetails?.sessions_limit,
    clientDetails?.sessions_limit_by_program,
    clientDetails?.sessions_remaining,
    clientDetails?.sessions_used,
    clientDetails?.sessions_used_by_program,
    customerPropertyId,
  ])

  const isLowSessionRemaining = sessionsRemaining < WARNING_THRESHOLD && sessionsRemaining > 0
  const hasNoHPI =
    healthPlanStatus === undefined ||
    healthPlanStatus === healthPlanStates.NOT_READY ||
    healthPlanStatus === healthPlanStates.HEALTH_PLAN_PAYMENT_MISSING ||
    healthPlanStatus === healthPlanStates.NOT_ELIGIBLE

  const lowSessionsRemainingText = useMemo(
    () =>
      `Please note this client has only ${sessionsRemaining}${programType ? ' ' + programType : ''} ${sessionsRemaining === 1 ? 'session' : 'sessions'} left.`,
    [sessionsRemaining, programType],
  )

  const healthPlanLimitInfoText = useMemo(() => {
    const currentYear = new Date().getFullYear().toString()
    const currentProgramCoverageBreakdown =
      programCoverageBreakdown &&
      currentYear in programCoverageBreakdown &&
      customerPropertyId &&
      customerPropertyId in programCoverageBreakdown[currentYear] &&
      programCoverageBreakdown[currentYear][customerPropertyId as keyof YearProgramCoverage]

    let healthPlanLimitInfo = `The client has completed ${usedSessionCount} of ${sessionsLimit} free${
      programType ? ' ' + programType : ''
    } sessions. `
    if (!!currentProgramCoverageBreakdown && currentProgramCoverageBreakdown?.bhbSupported) {
      switch (healthPlanStatus) {
        case healthPlanStates.NOT_READY:
          healthPlanLimitInfo += `The client may be eligible to use their medical plan and credit card for co-pays beyond ${sessionsLimit}${
            programType ? ' ' + programType : ''
          } sessions. Ask the client to check their email or Lyra Profile to add their medical plan and payment information.`
          break
        case healthPlanStates.READY:
          healthPlanLimitInfo += `${
            programType ? `${capitalize(programType)} sessions` : 'Sessions'
          } beyond ${sessionsLimit} will be submitted to the client’s medical plan and Lyra will collect a co-pay from the client.`
          break
      }
    }
    if (
      !!currentProgramCoverageBreakdown &&
      currentProgramCoverageBreakdown?.specialtyBhbSupported &&
      healthPlanStatus === healthPlanStates.READY
    ) {
      healthPlanLimitInfo += `You will be asked to provide ICD-10 diagnoses after session ${sessionsLimit}, as required by the medical plan.`
    }
    return healthPlanLimitInfo
  }, [programCoverageBreakdown, customerPropertyId, usedSessionCount, sessionsLimit, programType, healthPlanStatus])

  const lowLicenseSessionRemainingText = useMemo(() => {
    if (!licenseBasedSessionInfo) {
      return ''
    }
    const programTypeLabel = LICENSE_BASED_PROGRAM_TYPE_DISPLAY_NAMES[licenseBasedSessionInfo.program_type]
    if (!programTypeLabel) {
      return ''
    }
    return `Please note this client has only ${licenseBasedSessionInfo.sessions_remaining} ${programTypeLabel} ${licenseBasedSessionInfo.sessions_remaining === 1 ? 'session' : 'sessions'} left.`
  }, [licenseBasedSessionInfo])

  return {
    showLowSessionsWarning: isLowSessionRemaining && hasNoHPI && !isLicenseBased,
    showHealthPlanLimitInfo: isLowSessionRemaining && isNil(openBhbSessionLimit) && !isLicenseBased,
    showLowLicenseSessionWarning: isLowLicenseSessionRemaining,
    lowSessionsRemainingText,
    healthPlanLimitInfoText,
    lowLicenseSessionRemainingText,
  }
}
